<template>
  <div>
    <div class="d-flex justify-end mb-3">
      <v-btn
        color="primary"
        small
        outlined
        @click="downloadCSV"
        :loading="csvLoading"
        :disabled="csvLoading || !portCalls || portCalls.length === 0"
      >
        <v-icon left small>mdi-download</v-icon>
        Download CSV
      </v-btn>
    </div>

    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2 text-caption">Loading port call details...</div>
    </div>

    <div v-else-if="!portCalls || portCalls.length === 0" class="text-center py-8">
      <v-icon size="48" color="grey lighten-1">mdi-ship-wheel</v-icon>
      <div class="mt-2 text-h6 grey--text">No port calls found</div>
    </div>

    <v-data-table
      v-else
      :headers="headers"
      :items="portCalls"
      :loading="loading"
      :page.sync="pagination.page"
      :items-per-page.sync="pagination.itemsPerPage"
      :server-items-length="pagination.totalCount"
      :footer-props="{
        'items-per-page-options': [10, 25, 50, 100]
      }"
      @update:items-per-page="onItemsPerPageChange"
      @update:page="onPageChange"
      hide-default-footer
      class="elevation-0"
    >
      <template v-slot:item.job_name="{ item }">
        <v-btn text color="primary" small @click="searchByJobNumber(item)">
          {{ item.job_name }}
        </v-btn>
      </template>
      <template v-slot:item.vessel_name="{ item }">
        <v-btn text color="primary" small @click="searchByVessel(item)">
          {{ item.vessel_name }}
        </v-btn>
      </template>
      <template v-slot:item.customer_name="{ item }">
        <v-btn text color="primary" small @click="searchByCustomer(item)">
          {{ item.customer_name }}
        </v-btn>
      </template>
    </v-data-table>

    <div v-if="portCalls && portCalls.length > 0" class="d-flex justify-space-between align-center mt-4">
      <div class="text-caption grey--text">
        Showing {{ ((pagination.page - 1) * pagination.itemsPerPage) + 1 }} to
        {{ Math.min(pagination.page * pagination.itemsPerPage, pagination.totalCount) }}
        of {{ pagination.totalCount }} entries
      </div>
      <div>
        <v-btn icon small :disabled="pagination.page === 1" @click="pagination.page = 1; loadData()">
          <v-icon>mdi-page-first</v-icon>
        </v-btn>
        <v-btn icon small :disabled="pagination.page === 1" @click="pagination.page--; loadData()">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <span class="mx-2">{{ pagination.page }} / {{ pagination.totalPages }}</span>
        <v-btn icon small :disabled="pagination.page >= pagination.totalPages" @click="pagination.page++; loadData()">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn icon small :disabled="pagination.page >= pagination.totalPages" @click="pagination.page = pagination.totalPages; loadData()">
          <v-icon>mdi-page-last</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PortCallDetailReport',
  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    groupBy: {
      type: String,
      default: 'service'
    }
  },
  data() {
    return {
      loading: false,
      csvLoading: false,
      portCalls: [],
      pagination: {
        page: 1,
        itemsPerPage: 25,
        totalCount: 0,
        totalPages: 1
      },
    };
  },
  computed: {
    headers() {
      const baseHeaders = [
        { text: 'Job Number', value: 'job_name', align: 'start' },
        { text: 'Vessel', value: 'vessel_name', align: 'start' },
        { text: 'Customer', value: 'customer_name', align: 'start' },
        { text: 'Job Scope', value: 'job_scope', align: 'start' }
      ];
      
      const hasServiceColumn = this.portCalls && this.portCalls.length > 0 && 
                               this.portCalls.some(pc => pc.service_name !== undefined && pc.service_name !== null);
      
      if (hasServiceColumn) {
        baseHeaders.push({ text: 'Service', value: 'service_name', align: 'start' });
      }
      
      const hasSupplierColumn = this.portCalls && this.portCalls.length > 0 && 
                                this.portCalls.some(pc => pc.supplier_name !== undefined && pc.supplier_name !== null);
      
      if (hasSupplierColumn) {
        baseHeaders.push({ text: 'Supplier', value: 'supplier_name', align: 'start' });
      }
      
      baseHeaders.push(
        { text: 'ETA', value: 'eta', align: 'center' },
        { text: 'ETD', value: 'etd', align: 'center' },
        { text: 'Status', value: 'status', align: 'center' }
      );
      
      return baseHeaders;
    }
  },
  watch: {
    filters: {
      handler() {
        this.pagination.page = 1;
        this.loadData();
      },
      deep: true
    }
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          per_page: this.pagination.itemsPerPage
        };
        
        if (this.filters) {
          if (this.filters.vessel_id) params.vessel_id = this.filters.vessel_id;
          if (this.filters.customer_id) params.customer_id = this.filters.customer_id;
          if (this.filters.job_scope) params.job_scope = this.filters.job_scope;
          if (this.filters.service_id) params.service_id = this.filters.service_id;
          if (this.filters.supplier_id) params.supplier_id = this.filters.supplier_id;
          if (this.filters.start_date) params.start_date = this.filters.start_date;
          if (this.filters.end_date) params.end_date = this.filters.end_date;
        }
        
        const response = await this.$api.reports.getPortCallDetails(params);
        const data = response.result || response;
        
        this.portCalls = data.port_calls || [];
        this.pagination.totalCount = data.total_count || 0;
        this.pagination.totalPages = data.total_pages || 1;
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load port call details: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },
    onPageChange(page) {
      this.pagination.page = page;
      this.loadData();
    },
    onItemsPerPageChange(itemsPerPage) {
      this.pagination.itemsPerPage = itemsPerPage;
      this.pagination.page = 1;
      this.loadData();
    },
    viewJob(job) {
      this.$router.push({ name: 'operations', query: { operationId: job.operation_id } });
    },
    
    searchByJobNumber(item) {
      const url = this.$router.resolve({
        name: 'port calls',
        query: {
          search: item.job_name,
          jobStatus: 'All'
        }
      }).href;
      window.open(url, '_blank');
    },
    
    searchByVessel(item) {
      const url = this.$router.resolve({
        name: 'port calls',
        query: {
          search: item.vessel_name,
          jobStatus: 'All'
        }
      }).href;
      window.open(url, '_blank');
    },
    
    searchByCustomer(item) {
      const url = this.$router.resolve({
        name: 'port calls',
        query: {
          search: item.customer_name,
          jobStatus: 'All'
        }
      }).href;
      window.open(url, '_blank');
    },
    
    async downloadCSV() {
      this.csvLoading = true;
      try {
        const params = {
          download_csv: 'true'
        };
        
        if (this.filters) {
          if (this.filters.vessel_id) params.vessel_id = this.filters.vessel_id;
          if (this.filters.customer_id) params.customer_id = this.filters.customer_id;
          if (this.filters.job_scope) params.job_scope = this.filters.job_scope;
          if (this.filters.service_id) params.service_id = this.filters.service_id;
          if (this.filters.supplier_id) params.supplier_id = this.filters.supplier_id;
          if (this.filters.start_date) params.start_date = this.filters.start_date;
          if (this.filters.end_date) params.end_date = this.filters.end_date;
        }
        
        const queryString = Object.keys(params)
          .filter(key => params[key] != null && params[key] !== '')
          .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
          .join('&');
        
        const url = `/api/v1/reports/port_call_details.csv?${queryString}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'text/csv',
          },
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to download CSV: ${errorText}`);
        }
        
        const blob = await response.blob();
        const url_blob = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url_blob;
        link.setAttribute('download', `port_call_details_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url_blob);
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to download CSV: ${error.message}`,
        });
      } finally {
        this.csvLoading = false;
      }
    }
  },
  created() {
    this.loadData();
  }
};
</script>

